// Adapted from davinci-elections apps/web/src/theme/recipes/form.ts @ 6e23f58
import { defineRecipe, defineSlotRecipe } from '@chakra-ui/react'
import { fieldAnatomy, nativeSelectAnatomy, switchAnatomy } from '@chakra-ui/react/anatomy'

/** The idle look of a form field (R113): filled, a visible outline, a darker outline on hover. */
export const field = {
  bg: 'bg.field',
  borderColor: 'border.input',
  _hover: { borderColor: 'fg.muted' },
} as const

export const formLabel = defineRecipe({
  base: {
    fontSize: 'sm',
    fontWeight: 'normal',
  },
})

/** A native select: the same filled field with a visible outline as a text input. */
export const nativeSelect = defineSlotRecipe({
  slots: nativeSelectAnatomy.keys(),
  variants: {
    variant: {
      outline: { field },
    },
  },
})

/** A field label sits above its control at 500 weight, so label, value and helper text are three distinct roles. */
export const fieldRecipe = defineSlotRecipe({
  slots: fieldAnatomy.keys(),
  base: {
    label: { fontWeight: 'bold' },
  },
})

const baseStyle = {
  textStyle: 'md',
  _placeholder: {
    color: 'input.placeholder',
    textStyle: 'md',
  },
}

const xxl = {
  minH: 'var(--ipc-control-h)',
  textStyle: '2xl',
  _placeholder: {
    textStyle: '2xl',
  },
}

// iOS Safari auto-zooms focused inputs with a font-size below 16px: `md` (16px) on mobile and the compact `sm`
// from `md` up (vocdoni-app), which is also 16 px on the member pages. The minimum height is `--ipc-control-h`:
// 48 px touch targets for members, 40 px in the admin area.
// Set at variant level so it overrides Chakra's default size variants (textStyle `sm`).
const mobileSafeFontSize = {
  fontSize: { base: 'md', md: 'sm' },
  minH: 'var(--ipc-control-h)',
  _placeholder: {
    fontSize: { base: 'md', md: 'sm' },
  },
}

const sm = {
  borderRadius: 'sm',
  ...mobileSafeFontSize,
}

const md = {
  borderRadius: 'sm',
  ...mobileSafeFontSize,
}

export const input = defineRecipe({
  base: baseStyle,
  variants: {
    size: {
      sm,
      md,
      '2xl': xxl,
    },
    variant: {
      // A filled field (the content surface) with an outline at 3:1 against every surface it sits on (`border.input`),
      // darker on hover; the focus ring is the accent.
      outline: field,
      borderless: {
        border: 'none',
        px: 0,
        bg: 'transparent',
      },
    },
  },
})

export const textarea = defineRecipe({
  base: {
    fontSize: 'md',
    _placeholder: {
      color: 'input.placeholder',
      fontSize: 'md',
    },
  },
  variants: {
    size: {
      sm: mobileSafeFontSize,
      md: mobileSafeFontSize,
    },
    variant: {
      outline: field,
      borderless: {
        border: 'none',
        px: 0,
      },
    },
  },
})

export const switchRecipe = defineSlotRecipe({
  slots: switchAnatomy.keys(),
  base: {
    control: {
      _focusVisible: {
        _dark: {
          boxShadow: '0 0 0 2px {colors.gray.400}',
        },
      },
    },
    thumb: {
      _dark: {
        bg: 'colorPalette.600',
      },
    },
  },
})
